import portrait from "@/assets/tamal-portrait.webp";
import portraitOrb from "@/assets/tamal-orb.webp";
import { Link } from "@tanstack/react-router";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Section";
import { AUTHORITY, STEPS } from "@/components/home/home-content";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { lazy, Suspense } from "react";
const AICitations = lazy(() =>
  import("@/components/AICitations").then((m) => ({ default: m.AICitations })),
);

export function HomeAbout() {
  return (
    <>
      {/* MEET TAMAL */}
      <Section>
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute inset-0 -z-10 mx-auto rounded-full bg-primary/20 blur-3xl animate-blob" />
              <img
                src={portraitOrb}
                alt="Tamal — SEO, AEO & GEO expert from Rangpur, Bangladesh"
                loading="lazy"
                decoding="async"
                width={900}
                height={600}
                className="mx-auto w-full max-w-md drop-shadow-[0_30px_60px_rgba(198,248,78,0.18)] animate-float"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-primary">Meet Tamal</div>
              <h2 className="mt-3 font-display text-4xl md:text-6xl leading-[1.05]">
                The operator behind <span className="text-gradient">Freelancer Tamal.</span>
              </h2>
              <p className="mt-5 text-muted-foreground md:text-lg">
                I'm Tamal — a senior SEO strategist from Rangpur, Bangladesh.
                I help founders, SaaS teams and local businesses plan
                technical SEO, useful content and AI-visibility checks.
                Results are measured against each business's own baseline.
              </p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {[
                  "Technical SEO and content strategy",
                  "Local and international SEO planning",
                  "Specialist in AEO & GEO for AI engines",
                  "Practical SEO guides and training",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {b}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:shadow-[var(--shadow-glow)] transition">
                  About me <ArrowUpRight size={14} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-sm hover:bg-card transition">
                  Work with me
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* PROCESS */}
      <Section eyebrow="Process" title={<>A system, <span className="text-gradient">not a sprint.</span></>}>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4 relative">
          <div className="hidden lg:block absolute top-12 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          {STEPS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1}>
              <div className="group relative h-full rounded-2xl glass p-6 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] lift-on-hover hover:-translate-y-1.5">
                <div className="font-mono text-xs text-primary">0{i + 1}</div>
                <div className="mt-4 inline-grid place-items-center size-10 rounded-xl bg-primary/10 text-primary group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* AI CITATIONS */}
      <Section
        eyebrow="AEO targets"
        title={<>The prompts I optimize <span className="text-gradient">answers for.</span></>}
        subtitle="Sample queries across ChatGPT, Perplexity, Gemini and Claude — and the on-site pages I've structured (schema, entity signals, quotable answer blocks) to be the cited source."
      >
        <Reveal>
          <Suspense fallback={null}>
            <AICitations />
          </Suspense>
        </Reveal>
        <div className="mt-8 text-sm text-muted-foreground">
          Want this for your brand?{" "}
          <Link
            to="/services/aeo-geo"
            className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary"
          >
            See the dedicated AEO &amp; GEO service →
          </Link>
        </div>
      </Section>

      {/* AUTHORITY */}
      <Section>
        <Reveal>
          <div className="rounded-3xl glass p-8 md:p-12 overflow-hidden">
            <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-primary">Authority</div>
                <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
                  Technical, practical, <span className="text-gradient">accountable.</span>
                </h3>
                <p className="mt-3 text-muted-foreground">
                  Clear explanations, accurate business information and evidence you can inspect — not unsupported awards or guarantees.
                </p>
              </div>
              <div className="hidden md:block relative self-end md:-mb-[66px] lg:-mb-[78px]">
                <div className="absolute inset-0 -z-10 rounded-full bg-primary/20 blur-2xl" />
                <img src={portrait} alt="Tamal — SEO consultant" loading="lazy" decoding="async" width={900} height={485}
                  className="h-72 w-48 lg:h-[30rem] lg:w-80 object-cover object-center drop-shadow-[0_20px_40px_rgba(198,248,78,0.18)]" />
              </div>
              <ul className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-1 xl:grid-cols-2">
                {AUTHORITY.map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span className="min-w-0">{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* COUNTER STRIP */}
      <Section className="!pt-0">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-3xl overflow-hidden border border-border">
          {[
            { v: "SEO", l: "Technical & content" },
            { v: "AEO", l: "Answer visibility" },
            { v: "GEO", l: "Generative search" },
            { v: "EN / BN", l: "Bilingual content" },
          ].map((s) => (
            <div key={s.l} className="bg-background p-8">
              <div className="font-display text-3xl lg:text-4xl text-gradient">
                {s.v}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
}
